import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/outline";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { DateTime } from "luxon";
import { useCallback, useMemo, useRef } from "react";
import { Helmet } from "react-helmet";
import { toast } from "react-hot-toast";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { Accession, Case, LISCase, LISCaseList } from "pwapi";

import { ApiError } from "pwapi/core/ApiError";

import { useHasLoaded } from "shared/hooks/requestStatusHooks";
import { selectFeatureFlag } from "shared/reducers/sysReducer";
import selectLabClinics from "shared/selectors/selectLabClinics";

import { createAccession } from "app/actions/accessionActions";
import { getModMedCase, listCases } from "app/actions/caseActions";
import { scanSpecimen } from "app/actions/specimenActions";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { ReactComponent as LogoSVG } from "assets/logo.svg";
import { useBarcodePrintingCallbacks } from "hooks/useBarcodePrintingCallbacks";
import useBarcodeScanner from "hooks/useBarcodeScanner";
import {
  useSelectedLab,
  useSelectedLabStation,
} from "hooks/useSelectedLabStation";
import { defaultAccession } from "stations/AccessioningStationEdit";
import AccessionNumber from "ui/components/AccessionNumber";
import AppSpinner from "ui/components/AppSpinner";
import CasePriority from "ui/components/CasePriority";
import ClinicName from "ui/components/ClinicName";
import ErrorToastBody from "ui/components/ErrorToastBody";
import SpecimenProtocolTags from "ui/components/SpecimenProtocolTags";
import UserFullName from "ui/components/UserFullName";
import {
  ModMedPWPayload,
  accessionFromPayload,
  mmBarcodeFromSpecimen,
  modMedFromState,
} from "util/objectFromBarcode";
import { specimenSort } from "util/sortFunctions";
import { workProcessState } from "util/workProcessUtils";

import useCaseAccessionFilters from "./useCaseAccessionFilters";

const CaseAccession = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { state: navigationState } = useLocation();
  const casesState = useAppSelector((state) => state.casesWithWork);
  const sortedCaseIds = useAppSelector((state) => state.sys.caseListSortedIds);
  const cases = useMemo(() => {
    const results = Object.values(casesState);
    if (sortedCaseIds)
      results.sort(
        (a, b) =>
          sortedCaseIds.indexOf(a.id || 0) - sortedCaseIds.indexOf(b.id || 0)
      );
    return results;
  }, [casesState, sortedCaseIds]);
  const lab = useSelectedLab();
  const station = useSelectedLabStation();
  const casesReady = useHasLoaded(listCases, {
    parentLookupLabStationId: station.id,
  });
  const clinics = useAppSelector((state) => selectLabClinics(state));

  const users = useAppSelector((sys) => sys.users);

  const columnHelper = createColumnHelper<LISCaseList>();

  // TODO: we should have backend guarantees that collected_date is available
  // ... so this is "just in case" but could be removed
  // I am not sure about this actually. Denormalizing on the backend is far less efficient than doing so on the frontend
  // and we should have everything we need here already. That being said, I am not actually sure what Case.collected_date
  // is supposed to be denormalizing. - Tyler
  const case_collected_date = (row: LISCaseList) => {
    return row.collected_date
      ? row.collected_date
      : row.specimens?.at(0)?.collected_date;
  };

  const onScanModMedSpecimen = useCallback(
    (mod_med_payload: ModMedPWPayload) => {
      dispatch(
        getModMedCase({
          parentLookupLabStationId: station.id,
          externalOrderId: mod_med_payload.external_order_id,
          medicalRecordNumber: mod_med_payload.medical_record_number,
          externalPatientId: mod_med_payload.external_patient_id,
          firstName: mod_med_payload.first_name,
          lastName: mod_med_payload.last_name,
        })
      )
        .then((response: LISCase) => {
          navigate(`/cases/${response.id}`);

          if (mod_med_payload.specimen_name) {
            const specimen = response.specimens.find(
              (s) => s.name === mod_med_payload?.specimen_name
            );

            if (specimen?.id) {
              dispatch(
                scanSpecimen({
                  id: specimen.id,
                  parentLookupLabStationId: station.id,
                })
              );
            } else {
              toast.error(
                `Specimen ${mod_med_payload.specimen_name} was not found in Case ${response.accession}`
              );
            }
          }
        })
        .catch((error) => {
          if (error.status === 404 && error.body?.code === "TooManyResults") {
            toast.error(
              `Could not uniquely identify ${mod_med_payload.external_order_id}`
            );
          } else if (error.status === 404) {
            dispatch(
              createAccession({
                parentLookupLabStationId: station.id,
                requestBody: defaultAccession(lab, station, {
                  ...accessionFromPayload(mod_med_payload),
                  received_date: DateTime.now().toISO(),
                }),
              })
            )
              .then((accession: Accession) => {
                navigate(
                  `/station/accessioning/${accession.id}?integration=modmed`
                );
              })
              // TODO: this is a lot of c&p boilerplate...
              // How can we make this a pattern that stays out of individual components?
              .catch((e: ApiError) => {
                e.message &&
                  toast.error(
                    (t) => (
                      <ErrorToastBody
                        e={e}
                        handleClose={() => toast.dismiss(t.id)}
                      />
                    ),
                    {
                      position: "bottom-right",
                      className: "Toast",
                    }
                  );
              });
          }
        });
    },
    [cases, station, lab]
  );

  const onScanSpecimen = useCallback(
    (specimen_id: number) => {
      dispatch(
        scanSpecimen({
          id: specimen_id,
          parentLookupLabStationId: station.id,
        })
      ).then((response) => {
        navigate(`/cases/${response?.id}`);
      });
    },
    [cases, station]
  );

  useBarcodeScanner((barcode) => {
    if (barcode.object_type === "specimen" && barcode.mod_med_payload) {
      onScanModMedSpecimen(barcode.mod_med_payload);
    } else if (barcode.object_type === "specimen") {
      onScanSpecimen(barcode.object_id);
    }
  });

  const { printSpecimen } = useBarcodePrintingCallbacks();

  const lisAltClickScanning = useAppSelector((state) =>
    selectFeatureFlag(state, "lisAltClickScanning")
  );

  const columns = [
    // NOTE: this was pretty ugly in my opinion. Leaving it here for posterity.
    // ... Displayed inline next to accession instead
    // columnHelper.accessor((row) => row.priority, {
    //   id: "priority",
    //   header: "",
    //   enableColumnFilter: true,
    //   filterFn: (row, _id, value) => {
    //     return row.original.priority === value;
    //   },
    //   cell: ({ row }) => <CasePriority priorityCase={row.original} />,
    // }),
    columnHelper.accessor(
      (row) => `${row.accession ?? ""} ${row.external_order_id ?? ""}`,
      {
        id: "accession",
        header: "Status & Accession",
        enableColumnFilter: true,
        filterFn: (row, _id, value) => {
          if (value) {
            return row.original.status === value;
          }
          return true;
        },
        cell: ({ row }) => {
          const accessioningWorkProcess = row.original.work_processes.find(
            (w) => !w.order && w.tasks.find((w) => w.type === "grossing")
          );
          return (
            <>
              {row.original.status === Case.status.WORKING ? (
                <>
                  <div className="Tag Tag--green">accessioned</div>
                  {accessioningWorkProcess && (
                    <div className="Tag Tag--purple">
                      {workProcessState(accessioningWorkProcess)}
                    </div>
                  )}
                </>
              ) : (
                <div className="Tag Tag--blue">{row.original.status}</div>
              )}

              <div className="Cases__row__accession">
                <CasePriority priorityCase={row.original} />

                {row.original.accession && (
                  <>
                    <AccessionNumber accession={row.original.accession} />
                  </>
                )}
              </div>
            </>
          );
        },
      }
    ),

    columnHelper.accessor(
      (row) =>
        `${row.patient?.last_name}, ${row.patient?.first_name} ${
          row.patient?.date_of_birth ?? ""
        } ${row.accession ?? ""} ${row.external_order_id ?? ""}`,
      {
        cell: ({ row }) => {
          const { patient } = row.original;
          return (
            <>
              <strong>
                {patient?.last_name}, {patient?.first_name}
              </strong>
              <br />
              {patient?.date_of_birth && (
                <span>
                  {DateTime.fromFormat(
                    patient?.date_of_birth,
                    "yyyy-MM-dd"
                  ).toLocaleString()}
                </span>
              )}
            </>
          );
        },
        id: "patient__last_name,patient__first_name",
        header: "Patient",
      }
    ),

    columnHelper.accessor(
      (row) => {
        const collected_date = case_collected_date(row);
        return (
          collected_date && DateTime.fromISO(collected_date).toLocaleString()
        );
      },
      {
        id: "specimens__collected_date",
        header: "Collected",
        enableColumnFilter: true,
        filterFn: (row, _id, value) => {
          return !!case_collected_date(row.original)?.includes(value);
        },
        cell: ({ getValue, row }) => {
          const collected_date = case_collected_date(row.original);
          const diffDate = collected_date
            ? DateTime.fromISO(collected_date).toRelative()
            : "";
          return (
            <div>
              {getValue()}
              <br />
              <span>{diffDate}</span>
            </div>
          );
        },
      }
    ),

    columnHelper.accessor(
      (row) => {
        const ordering_doctor = users[row.ordering_doctor_id || 0];
        return `${clinics[row.patient?.clinic_id || 0]?.name} ${
          ordering_doctor?.first_name
        } ${ordering_doctor?.last_name}`;
      },
      {
        cell: ({ row }) => {
          return (
            <>
              <strong>
                <ClinicName id={row.original.patient?.clinic_id} />
              </strong>
              <br />
              <span>
                <UserFullName id={row.original.ordering_doctor_id} />
              </span>
            </>
          );
        },
        header: "Provider",
        id: "patient__clinic__name",
        enableColumnFilter: true,
        filterFn: "includesString",
      }
    ),

    columnHelper.accessor(
      (row) => row.specimens?.map((s) => `${s.name} ${s.site}`).join(" "),
      {
        header: "Specimens",
        id: "specimens",
        enableSorting: false,
        cell: ({ row }) => {
          const { specimens } = row.original;
          return specimens && specimens.length > 0 ? (
            <>
              {[...specimens].sort(specimenSort).map((s) => (
                <div
                  key={s.id}
                  className="Scannable"
                  onClick={(e) => {
                    if (e.altKey && lisAltClickScanning) {
                      row.original.external_order_id
                        ? onScanModMedSpecimen(modMedFromState(row.original, s))
                        : s.id && onScanSpecimen(s.id);
                    }
                  }}
                >
                  <strong>{s.name}</strong> &mdash; {s.site}{" "}
                  {s.lis_state && (
                    <div className="Tag Tag--green">{s.lis_state}</div>
                  )}
                  <SpecimenProtocolTags specimen={s} specimens={specimens} />
                  {/* TODO: refactor out logic of which lis state to which colour */}
                </div>
              ))}
            </>
          ) : (
            <></>
          );
        },
      }
    ),

    columnHelper.display({
      id: "actions",
      cell: ({ row }) => (
        <Link
          to={`/cases/${row.original.id}`}
          state={{
            from: "case-accession",
            search: window.location.search,
          }}
          className="Scannable"
          onClick={(e) => {
            if (e.altKey && lisAltClickScanning) {
              e.preventDefault();

              if (row.original.external_order_id) {
                window.open(
                  `/print_object/modmed/${row.original.specimens
                    ?.map((s) =>
                      encodeURIComponent(mmBarcodeFromSpecimen(row.original, s))
                    )
                    .join(",")}`
                );
              } else {
                printSpecimen(
                  row.original.specimens.map((s) => s.id) as number[],
                  row.original.id as number
                );
              }
            }
          }}
        >
          View &rarr;
        </Link>
      ),
    }),
  ];

  const dateFilter = useRef<HTMLInputElement>(null);

  const {
    filters,
    updateFilters,
    updateFiltersWithDebounce,
    setSorting,
    clearFilters,
  } = useCaseAccessionFilters();

  const table = useReactTable({
    data: cases,
    columns,
    state: {
      globalFilter: filters.search,
      sorting: filters.sorting,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: (value) => {
      updateFilters("search", value);
    },

    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    pageCount: filters?.pageCount || 1,
    debugAll: false,
  });

  if (!casesReady) return <AppSpinner>Loading Cases...</AppSpinner>;

  return (
    <div className="Cases">
      <Helmet>
        <title>Cases</title>
      </Helmet>
      <header className="Cases__header">
        <figure className="Logo">
          <LogoSVG />
        </figure>

        <div>
          <p>
            <strong>{lab?.name}</strong>
          </p>
          <p>{station?.name}</p>
        </div>

        <Link
          to="/station/accessioning/new"
          state={{ from: "case-accession", search: window.location.search }}
        >
          Start New Accession
        </Link>
      </header>
      <nav>
        <Link to={`/cases${navigationState}`}>
          <strong>Cases</strong>
        </Link>
        <Link
          to="/users"
          state={{ from: "case-accession", search: window.location.search }}
        >
          Users
        </Link>
      </nav>

      <div className="Cases__search">
        <div className="Cases__filter Cases__search--statusFilter">
          <label
            htmlFor="status-filter-select"
            className="Cases__filter__label"
          >
            Status
          </label>
          <select
            onChange={(e) => updateFilters("status", e.target.value)}
            value={filters.status || ""}
            id="status-filter-select"
          >
            <option value="">All Cases</option>
            {[
              Case.status.ORDERED,
              Case.status.WORKING,
              Case.status.ACTIVE,
              Case.status.FINALIZED,
            ].map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>
        <div className="Cases__filter Cases__search--filters">
          <label
            htmlFor="clinic-filter-select"
            className="Cases__filter__label"
          >
            Clinic
          </label>
          <select
            value={filters.clinicId || ""}
            onChange={(e) => {
              updateFilters(
                "clinicId",
                e.target.value ? parseInt(e.target.value) : undefined
              );
            }}
            id="clinic-filter-select"
          >
            <option>All Clinics</option>
            {Object.values(clinics)
              .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""))
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
          </select>
        </div>
        <div className="Cases__filter Cases__search--globalFilter">
          <span className="Cases__filter__label">
            <label htmlFor="search-filter">
              Case Accession / Clinic Name / Patient Name
            </label>
            <button
              className="Button Button--text Button__text--blue Button__text--noDec Cases__Button--clearFilter"
              onClick={() => updateFilters("search", "")}
            >
              Clear
            </button>
          </span>
          <input
            type="text"
            value={filters.search || ""}
            onChange={(e) => {
              updateFiltersWithDebounce("search", e.target.value);
            }}
            id="search-filter"
            placeholder="Search"
          />
        </div>
        <div className="Cases__filter Cases__search--filters">
          <span className="Cases__filter__label">
            <label htmlFor="collected-at-filter">Collected Date</label>
            <button
              className="Button Button--text Button__text--blue Button__text--noDec Cases__Button--clearFilter"
              onClick={() => {
                updateFilters("collectedAt", "");
                // Date input holds onto incomplete dates and does not do an onChange until a date is fully complete
                if (dateFilter.current) dateFilter.current.value = "";
              }}
            >
              Clear
            </button>
          </span>
          <input
            type="date"
            ref={dateFilter}
            placeholder="Collected Date"
            value={filters.collectedAt || ""}
            onChange={(e) => {
              updateFilters("collectedAt", e.target.value);
            }}
            max="9999-12-31"
            id="collected-at-filter"
          />
        </div>
        <button
          className="Button Cases__Button--reset"
          onClick={() => {
            clearFilters();
            // Date input holds onto incomplete dates and does not do an onChange until a date is fully complete
            if (dateFilter.current) dateFilter.current.value = "";
          }}
        >
          Reset Filters
        </button>
      </div>

      <div className="Cases__list">
        <table>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id}>
                    {header.isPlaceholder ? null : (
                      <div
                        {...{
                          className: header.column.getCanSort()
                            ? "cursor-pointer select-none"
                            : "",
                          onClick: header.column.getToggleSortingHandler(),
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: (
                            <>
                              <ChevronDownIcon />
                            </>
                          ),
                          desc: (
                            <>
                              <ChevronUpIcon />
                            </>
                          ),
                        }[header.column.getIsSorted() as string] ?? (
                          <svg height={0} width="1em"></svg>
                        )}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className={`Cases__td Cases__td--${cell}`}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        <div className="Cases__pagination">
          <div>
            <strong>
              {filters.currentPage} of {filters.pageCount || 0}
            </strong>
          </div>
          <div className="Cases__pagination__buttons">
            <button
              className="border rounded p-1"
              onClick={() => updateFilters("currentPage", 1)}
              disabled={filters.currentPage <= 1}
            >
              {"<<"}
            </button>
            <button
              className="border rounded p-1"
              onClick={() =>
                updateFilters("currentPage", filters.currentPage - 1)
              }
              disabled={filters.currentPage <= 1}
            >
              {"<"}
            </button>
            <button
              className="border rounded p-1"
              onClick={() =>
                updateFilters("currentPage", filters.currentPage + 1)
              }
              disabled={filters.currentPage >= filters.pageCount}
            >
              {">"}
            </button>
            <button
              className="border rounded p-1"
              onClick={() => updateFilters("currentPage", filters.pageCount)}
              disabled={filters.currentPage >= filters.pageCount}
            >
              {">>"}
            </button>
          </div>

          <select
            value={filters.pageSize}
            onChange={(e) => {
              updateFilters("pageSize", Number(e.target.value));
            }}
          >
            {[10, 20, 30, 40, 50].map((ps) => (
              <option key={ps} value={ps}>
                Show {ps}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

export default CaseAccession;
